import React, { useState } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Section } from '@/components/atoms/Section';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/form/Input';
import { computeText } from '@/locales/utils';
import type { LegalFormValues } from '../RestaurantInfo.viewmodel';
import { FormGridThreeCols, SectionActions, ChangesInfo, ErrorText } from '../RestaurantInfo.style';
import { AddressAutocomplete } from './AddressAutocomplete';

type LegalInfoSectionProps = {
    form: UseFormReturn<LegalFormValues>;
    changesCount: number;
    isSaving: boolean;
    onSave: () => Promise<void>;
    onCancel: () => void;
    legalAddressText: string;
    onLegalAddressChange: (address: { name: string; lat: number; lng: number; providerId: string }) => void;
};

export const LegalInfoSection: React.FunctionComponent<LegalInfoSectionProps> = ({
    form,
    changesCount,
    isSaving,
    onSave,
    onCancel,
    legalAddressText,
    onLegalAddressChange,
}) => {
    const intl = useIntl();
    const [saveError, setSaveError] = useState<string>();

    const handleSave = async () => {
        try {
            setSaveError(undefined);
            await onSave();
        } catch {
            setSaveError(computeText(intl, 'restaurantInfo.save.error'));
        }
    };

    return (
        <Section title="restaurantInfo.legal.title" description="restaurantInfo.legal.description">
            <FormProvider {...form}>
                <FormGridThreeCols>
                    <AddressAutocomplete
                        value={legalAddressText}
                        onChange={onLegalAddressChange}
                        labelId="restaurantInfo.legal.address"
                    />
                    <Input name="socialReason" labelId="restaurantInfo.legal.socialReason" />
                    <Input name="organizationType" labelId="restaurantInfo.legal.organizationType" />
                    <Input name="capital" labelId="restaurantInfo.legal.capital" type="number" />
                    <Input name="commerceRegistryCity" labelId="restaurantInfo.legal.commerceRegistryCity" />
                    <Input
                        name="siren"
                        labelId="restaurantInfo.legal.siren"
                        rules={{ required: computeText(intl, 'field.required') }}
                    />
                    <Input
                        name="taxNumber"
                        labelId="restaurantInfo.legal.taxNumber"
                        rules={{ required: computeText(intl, 'field.required') }}
                    />
                    <Input
                        name="industryClassificationCode"
                        labelId="restaurantInfo.legal.industryClassificationCode"
                        rules={{ required: computeText(intl, 'field.required') }}
                    />
                    <Input name="publicationDirectorName" labelId="restaurantInfo.legal.publicationDirectorName" />
                    <Input
                        name="publicationDirectorFunction"
                        labelId="restaurantInfo.legal.publicationDirectorFunction"
                    />
                    <Input name="webmasterName" labelId="restaurantInfo.legal.webmasterName" />
                    <Input
                        name="webmasterEmail"
                        labelId="restaurantInfo.legal.webmasterEmail"
                        rules={{
                            pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: computeText(intl, 'field.invalidEmail'),
                            },
                        }}
                    />
                </FormGridThreeCols>
            </FormProvider>
            {changesCount > 0 && (
                <SectionActions>
                    <ChangesInfo>
                        <FormattedMessage id="savingBar.changesDone" values={{ changesCount }} />
                    </ChangesInfo>
                    <Button buttonType="secondary" onClick={onCancel} isLoading={isSaving}>
                        <FormattedMessage id="button.cancel" />
                    </Button>
                    <Button buttonType="primary" onClick={handleSave} isLoading={isSaving}>
                        <FormattedMessage id="button.save" />
                    </Button>
                </SectionActions>
            )}
            {saveError && <ErrorText>{saveError}</ErrorText>}
        </Section>
    );
};
